import posthog from 'posthog-js'
import { env } from './environment'

// PostHog configuration
const POSTHOG_API_KEY = env.isProduction 
  ? import.meta.env.VITE_POSTHOG_API_KEY_PROD 
  : import.meta.env.VITE_POSTHOG_API_KEY_DEV

const POSTHOG_HOST = env.isProduction
  ? import.meta.env.VITE_POSTHOG_HOST_PROD || 'https://us.i.posthog.com'
  : import.meta.env.VITE_POSTHOG_HOST_DEV || 'https://us.i.posthog.com'

// Initialize PostHog
export const initPostHog = () => {
  console.log('PostHog initialization started...')
  console.log('API Key present:', !!POSTHOG_API_KEY)
  console.log('Host:', POSTHOG_HOST)
  
  if (!POSTHOG_API_KEY) {
    console.warn('PostHog API key not found. Analytics will be disabled.')
    return
  }

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: env.isDevelopment,
    opt_out_capturing_by_default: false,
    respect_dnt: true,
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: false,
    secure_cookie: env.isProduction,
    person_profiles: 'identified_only',
    loaded: (posthog) => {
      if (env.enableDebug) {
        console.log('PostHog loaded successfully')
        console.log('PostHog distinct_id:', posthog.get_distinct_id())
      }
      posthog.register({
        environment: env.name,
        app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      })
    }
  })

  if (env.enableDebug) {
    console.log('PostHog initialized with config:', {
      apiKey: POSTHOG_API_KEY ? '***' : 'not set',
      host: POSTHOG_HOST,
      environment: env.name,
    })
  }
}

// Export PostHog instance for direct usage
export { posthog }

// Helper functions for common analytics events
export const analytics = {
  identify: async (userId: string, properties?: Record<string, any>) => {
    if (!userId) {
      console.warn('PostHog: Cannot identify user without userId')
      return
    }

    // Wait for PostHog to be ready
    await waitForPostHog()

    console.log('PostHog: Identifying user:', { userId, properties })
    
    const userIdString = userId.toString()
    
    // ✅ CRITICAL: Call identify first, then set properties
    posthog.identify(userIdString, {
      email: properties?.email,
      name: properties?.name,
      username: properties?.username,
      ...properties,
      identified_at: new Date().toISOString(),
    })

    // ✅ IMPORTANT: Wait a bit for identification to process
    await new Promise(resolve => setTimeout(resolve, 100))

    // Verify identification worked
    const currentId = posthog.get_distinct_id()
    const isAnonymous = currentId.startsWith('$device:') || currentId.length > 30
    
    console.log('PostHog: Identification result:', {
      currentId,
      isAnonymous,
      expectedId: userIdString,
      success: currentId === userIdString
    })

    return currentId === userIdString
  },

  // Track custom events
  track: (eventName: string, properties?: Record<string, any>) => {
    if (!posthog.__loaded) {
      console.warn('PostHog: Not loaded yet, skipping event:', eventName)
      return
    }

    const currentUserId = posthog.get_distinct_id()
    posthog.capture(eventName, {
      ...properties,
      user_id: currentUserId,
      timestamp: new Date().toISOString(),
    })
  },

  // Track page views manually
  trackPageView: (pageName?: string) => {
    if (!posthog.__loaded) return
    
    posthog.capture('$pageview', { 
      page_name: pageName,
      user_id: posthog.get_distinct_id(),
      $current_url: window.location.href,
      $pathname: window.location.pathname,
    })
  },

  setUserProperties: (properties: Record<string, any>) => {
    if (!posthog.__loaded) {
      console.warn('PostHog: Not loaded yet, skipping user properties')
      return
    }
    
    console.log('PostHog: Setting user properties:', properties)
    posthog.setPersonProperties(properties)
  },

  // Track feature flags
  getFeatureFlag: (flagKey: string) => {
    if (!posthog.__loaded) return null
    return posthog.getFeatureFlag(flagKey)
  },

  // Track A/B test participation
  isFeatureEnabled: (flagKey: string) => {
    if (!posthog.__loaded) return false
    return posthog.isFeatureEnabled(flagKey)
  },

  // Reset user (for logout)
  reset: () => {
    if (!posthog.__loaded) return
    console.log('PostHog: Resetting user session')
    posthog.reset()
  },

  // Get current user info
  getCurrentUserId: () => {
    if (!posthog.__loaded) return null
    return posthog.get_distinct_id()
  },

  // Check if user is properly identified (not anonymous)
  isUserIdentified: () => {
    if (!posthog.__loaded) return false
    const distinctId = posthog.get_distinct_id()
    // PostHog anonymous IDs typically start with '$device:' or are very long UUIDs
    return distinctId && !distinctId.startsWith('$device:') && distinctId.length < 30
  },

  // Track user actions with better context
  trackUserAction: (action: string, context?: Record<string, any>) => {
    if (!posthog.__loaded) return
    
    posthog.capture('user_action', {
      action,
      user_id: posthog.get_distinct_id(),
      ...context,
      timestamp: new Date().toISOString(),
    })
  },

  // Specialized tracking methods
  trackEmailEvent: (eventType: string, emailData?: Record<string, any>) => {
    if (!posthog.__loaded) return
    
    posthog.capture('email_event', {
      event_type: eventType,
      user_id: posthog.get_distinct_id(),
      ...emailData,
      timestamp: new Date().toISOString(),
    })
  },

  trackPlayerEvent: (eventType: string, playerData?: Record<string, any>) => {
    if (!posthog.__loaded) return
    
    posthog.capture('player_event', {
      event_type: eventType,
      user_id: posthog.get_distinct_id(),
      ...playerData,
      timestamp: new Date().toISOString(),
    })
  },

  trackSubscriptionEvent: (eventType: string, subscriptionData?: Record<string, any>) => {
    if (!posthog.__loaded) return
    
    posthog.capture('subscription_event', {
      event_type: eventType,
      user_id: posthog.get_distinct_id(),
      ...subscriptionData,
      timestamp: new Date().toISOString(),
    })
  }
}

const waitForPostHog = (timeout = 5000): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (posthog.__loaded) {
      resolve()
      return
    }

    const startTime = Date.now()
    const checkInterval = setInterval(() => {
      if (posthog.__loaded) {
        clearInterval(checkInterval)
        resolve()
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval)
        reject(new Error('PostHog failed to load within timeout'))
      }
    }, 100)
  })
}

export default analytics